import React, { useState, useEffect } from "react";
import SEO from "../seo";
const LiveView = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "what-youll-be-able-to-do",
        "why-it-matters",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Live View | xPage Help Center"
        description="Discover the upcoming Live View feature in xPage. Monitor store traffic, track real-time sales, analyze visitor behavior, and make instant business decisions with live data insights."
        keywords="xPage, live view, real-time analytics, sales tracking, visitor monitoring, traffic insights, store performance, eCommerce dashboard, live stats"
        canonical="https://help.xpage.ai/help/business-expansion/Live%20View"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Live View – Coming Soon
          </h1>
          <p className="text-sm text-systemLabel">
            Experience your store in real time.
          </p>
          <p className="text-sm text-systemLabel">
            The upcoming Live View feature will give you a dynamic, instant look
            at your store's activity, helping you track performance, monitor
            visitor behavior, and make quick, informed decisions as they happen.
          </p>
        </header>

        {/* What You'll Be Able to Do Section */}
        <section id="what-youll-be-able-to-do" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            What You'll Be Able to Do (Coming Soon)
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Feature
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    <strong className="text-systemTitle text-sm">
                      Description
                    </strong>
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Real-Time Traffic Monitoring
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Watch live visitors as they browse your store, see where
                    they come from, what pages they're viewing, and how they
                    move through your site.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Sales Tracking
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Follow orders as they happen with up-to-the-minute updates
                    on purchases and cart activity.
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Geolocation Insights
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Visualize where your traffic and orders originate around the
                    world in real time.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Performance Metrics
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    View live stats like active sessions, conversions, and
                    engagement rates at a glance.
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Event Timeline
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Stay updated with a continuous feed of key store events and
                    actions.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Why It Matters Section */}
        <section id="why-it-matters" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Why It Matters
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Benefit
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    <strong className="text-systemTitle text-sm">
                      Description
                    </strong>
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      React Instantly
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Spot traffic spikes, active customers, or checkout issues as
                    they occur.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Optimize in Real Time
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Adjust campaigns, promotions, or store content based on live
                    user behavior.
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Stay in Control
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Get a continuous overview of your store's health and
                    performance, without waiting for reports.
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md">
              <p className="text-sm text-systemLabel">
                The Live View dashboard is coming soon: bringing you the power
                to see, understand, and act on what's happening in your store
                right now.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#what-youll-be-able-to-do"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "what-youll-be-able-to-do"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                What You'll Be Able to Do
              </a>
            </li>
            <li>
              <a
                href="#why-it-matters"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "why-it-matters"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Why It Matters
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default LiveView;
